import type { AxiosError } from "axios";

import type { ParamsConstraint } from "@/hooks/use-filter-params";

/**
 * Function type for cleaning stale filter values.
 * Compares current params against available filters and removes invalid values.
 */
export type StaleFilterCleaner<
  TParams extends ParamsConstraint,
  TAvailableFilters,
> = (
  currentParams: Partial<TParams>,
  availableFilters: TAvailableFilters,
) => StaleFilterCleanerResult<TParams>;

export interface StaleFilterCleanerResult<TParams> {
  cleaned: Partial<TParams>;
  hadStaleValues: boolean;
}

/**
 * State for tracking error recovery attempts.
 */
export interface RecoveryState {
  isRecovering: boolean;
  recoveryAttempted: boolean;
  lastError: AxiosError | null;
}

/**
 * HTTP status codes that indicate non-recoverable errors.
 * These errors are not caused by bad filter params and should not trigger recovery.
 */
export const NON_RECOVERABLE_STATUS_CODES = [401, 403, 404] as const;
export type NonRecoverableStatusCode =
  (typeof NON_RECOVERABLE_STATUS_CODES)[number];

/**
 * Check if an error status code is recoverable (might be caused by bad params).
 */
export const isRecoverableStatusCode = (status: number | undefined): boolean =>
  status !== undefined &&
  !NON_RECOVERABLE_STATUS_CODES.includes(status as NonRecoverableStatusCode);
